import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import Iconify from '../Iconify';
import { toImageSrc, resolveSignedMediaSrc, pdfViewerSrc } from '../../utils/imageProxy';
import VideoPlaybackBody from '../video/VideoPlaybackBody';
import { PdfViewer } from '../openPDFDialog';
import {
  articleMediaCloseButtonSx,
  articleMediaDialogContentSx,
} from '../articleFolder/articleMediaDialogStyles';
import { galleryAutoplayMs, gallerySwipeDirection, nextGalleryZoom } from '../../utils/contentMediaUtils';

/**
 * One slideshow for photos, videos and PDFs.
 * Horizontal arrows change media. PDF pages scroll vertically inside the slide.
 * Autoplay (5s) runs only while the current media is a photo.
 */
const STAGE_HEIGHT = '70vh';

/** Scales the media from its center. The stage box never changes size. */
const ConsultationZoomFrame = ({ zoom, children }) => (
  <Box
    sx={{
      height: STAGE_HEIGHT,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `scale(${zoom})`,
        transformOrigin: 'center center',
      }}
    >
      {children}
    </Box>
  </Box>
);

const ContentMediaViewer = ({ open, onClose, items = [], startIndex = 0 }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [index, setIndex] = useState(startIndex);
  const [autoplay, setAutoplay] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const pdfObjectUrl = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(startIndex);
      setAutoplay(true);
      setZoom(1);
    }
  }, [open, startIndex, items]);

  const current = items[index] || null;

  useEffect(() => {
    setZoom(1);
  }, [current?.id]);

  useEffect(() => {
    let cancelled = false;
    const releasePdf = () => {
      if (pdfObjectUrl.current) {
        URL.revokeObjectURL(pdfObjectUrl.current);
        pdfObjectUrl.current = null;
      }
    };

    if (!open || current?.kind !== 'document' || !current.mediaUrl) {
      releasePdf();
      setPdfUrl(null);
      setPdfError(null);
      return undefined;
    }

    setPdfError(null);
    (async () => {
      try {
        const signed = await resolveSignedMediaSrc(current.mediaUrl);
        const response = await fetch(pdfViewerSrc(signed));
        if (!response.ok) {
          throw new Error('missing');
        }
        const blob = await response.blob();
        if (cancelled) {
          return;
        }
        releasePdf();
        const objectUrl = URL.createObjectURL(blob);
        pdfObjectUrl.current = objectUrl;
        setPdfUrl(objectUrl);
      } catch {
        if (!cancelled) {
          releasePdf();
          setPdfUrl(null);
          setPdfError('Ce document est introuvable.');
        }
      }
    })();

    return () => {
      cancelled = true;
      releasePdf();
    };
  }, [open, current]);

  const changeZoom = (direction) => {
    setAutoplay(false);
    setZoom((value) => nextGalleryZoom(value, direction));
  };

  const go = useCallback((direction) => {
    setAutoplay(false);
    setIndex((value) => {
      const count = items.length;
      if (!count) {
        return 0;
      }
      return (value + direction + count) % count;
    });
  }, [items.length]);

  const autoplayMs = galleryAutoplayMs({
    open,
    autoplay,
    itemCount: items.length,
    kind: current?.kind,
  });

  useEffect(() => {
    if (!autoplayMs) {
      return undefined;
    }
    const timer = setInterval(() => {
      setIndex((value) => (value + 1) % items.length);
    }, autoplayMs);
    return () => clearInterval(timer);
  }, [autoplayMs, items.length]);

  const onTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (event) => {
    if (touchStartX.current == null) {
      return;
    }
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    const step = gallerySwipeDirection(delta, current?.kind);
    if (!step) {
      return;
    }
    go(step);
  };

  if (!open || !current) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogContent sx={articleMediaDialogContentSx}>
        <IconButton onClick={onClose} sx={articleMediaCloseButtonSx} aria-label="Fermer">
          <Iconify icon="material-symbols:close" />
        </IconButton>
        <Typography sx={{ fontFamily: 'Mulish', fontWeight: 700, pr: 5, mb: 1, wordBreak: 'break-word' }}>
          {current.label}
        </Typography>
        {items.length > 1 && (
          <>
            <IconButton onClick={() => go(-1)} aria-label="Média précédent" sx={{ position: 'absolute', top: '50%', left: 8, zIndex: 2, backgroundColor: 'rgba(255,255,255,0.9)' }}>
              <Iconify icon="material-symbols:chevron-left" />
            </IconButton>
            <IconButton onClick={() => go(1)} aria-label="Média suivant" sx={{ position: 'absolute', top: '50%', right: 8, zIndex: 2, backgroundColor: 'rgba(255,255,255,0.9)' }}>
              <Iconify icon="material-symbols:chevron-right" />
            </IconButton>
          </>
        )}
        <Box data-testid="content-media-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} sx={{ position: 'relative', minHeight: '40vh' }}>
          {(current.kind === 'photo' || (current.kind === 'document' && pdfUrl)) && (
            <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, display: 'flex', gap: 1 }}>
              <IconButton aria-label="Réduire" onClick={() => changeZoom(-1)} disabled={zoom <= 0.5} sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                <Iconify icon="material-symbols:zoom-out" />
              </IconButton>
              <IconButton aria-label="Agrandir" onClick={() => changeZoom(1)} disabled={zoom >= 3} sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                <Iconify icon="material-symbols:zoom-in" />
              </IconButton>
            </Box>
          )}
          {current.kind === 'photo' && (
            <ConsultationZoomFrame zoom={zoom}>
              <Box
                component="img"
                src={toImageSrc(current.mediaUrl)}
                alt={current.label}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  display: 'block',
                }}
              />
            </ConsultationZoomFrame>
          )}
          {current.kind === 'video' && (
            <VideoPlaybackBody key={current.id} video={current.playback} title={current.label} active />
          )}
          {current.kind === 'document' && pdfError && (
            <Typography sx={{ fontFamily: 'Mulish', py: 4, textAlign: 'center' }}>
              {pdfError}
            </Typography>
          )}
          {current.kind === 'document' && pdfUrl && (
            <ConsultationZoomFrame zoom={zoom}>
              <Box sx={{ width: '100%', height: '100%' }}>
                <PdfViewer fileUrl={pdfUrl} />
              </Box>
            </ConsultationZoomFrame>
          )}
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', color: 'text.secondary' }}>
          {index + 1} / {items.length}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default ContentMediaViewer;
